import { ref, Ref } from "vue"
import { postsApi, topicsApi } from "store/api"
import { ResponseError } from "interfaces/error.interface"
import { Emoji, Reaction } from "suus-api"
import { ReactionCounts } from "interfaces/reactionCounts.interface"
import { withTimeOut } from "helpers/delay"
import { PolymorphicType } from "features/common/shared/polymorphic"

export default function useReaction(
  reactionable: PolymorphicType,
  initialReactionCounts: Ref<ReactionCounts>,
  initialUserReaction: Ref<Emoji | undefined>
) {
  function reactionsApiFor(reactionable: PolymorphicType) {
    switch (reactionable.type) {
      case "Topic":
        return {
          // NB: A method looses its "this" binding when retrieving a reference only,
          // therefore we need to add the original this again. Javascript is awesome...
          createReaction: topicsApi.createTopicReaction.bind(topicsApi),
          deleteReaction: topicsApi.deleteTopicReaction.bind(topicsApi),
          getReactions: topicsApi.getTopicReactions.bind(topicsApi),
        }
      case "Post":
        return {
          createReaction: postsApi.createPostReaction.bind(postsApi),
          deleteReaction: postsApi.deletePostReaction.bind(postsApi),
          getReactions: postsApi.getPostReactions.bind(postsApi),
        }
      case "Comment":
        console.warn("Reactions for comments is not yet supported!")
        return {
          createReaction: null,
          deleteReaction: null,
          getReactions: null,
        }
      default:
        throw "Unknown polymorphic type: " + reactionable.type
    }
  }

  const api = reactionsApiFor(reactionable)
  const REMOVE_COUNT = -1
  const ADD_COUNT = 1
  const isPending: Ref<boolean> = ref(false)
  const error: Ref<ResponseError> = ref(null)
  const likesCount = ref(initialReactionCounts.value.likesCount)
  const ideasCount = ref(initialReactionCounts.value.ideasCount)
  const cheersCount = ref(initialReactionCounts.value.cheersCount)
  const userReaction = ref(initialUserReaction.value || undefined)
  const reactions: Ref<Reaction[]> = ref<Reaction[]>([])

  function fetchReactions(reactionable: PolymorphicType) {
    api.getReactions(reactionable.id.toString()).then((response) => {
      reactions.value = response.data
    })
  }

  fetchReactions(reactionable)

  function changeCount(reaction: Emoji, delta: number): void {
    switch (reaction) {
      case Emoji.Cheers:
        cheersCount.value = cheersCount.value + delta
        break
      case Emoji.Like:
        likesCount.value = likesCount.value + delta
        break
      case Emoji.Idea:
        ideasCount.value = ideasCount.value + delta
        break
      default:
        break
    }
  }

  function delayedChangeCount(reaction: Emoji, delta: number) {
    return withTimeOut(() => changeCount(reaction, delta), 800)
  }

  const createReaction = async (id: string, newReaction: Emoji) => {
    userReaction.value = newReaction
    delayedChangeCount(newReaction, ADD_COUNT)
    isPending.value = true
    error.value = null
    try {
      await api.createReaction(id, newReaction)
    } catch (error: ResponseError) {
      error.value = error
      userReaction.value = null
      delayedChangeCount(newReaction, REMOVE_COUNT)
    } finally {
      isPending.value = false
      fetchReactions(reactionable)
    }
  }
  async function updateReaction(
    id: string,
    newReaction: Emoji,
    prevReaction: Emoji
  ) {
    userReaction.value = newReaction
    delayedChangeCount(prevReaction, REMOVE_COUNT)
    delayedChangeCount(newReaction, ADD_COUNT)
    isPending.value = true
    error.value = null
    try {
      await api.deleteReaction(id, prevReaction)
      await api.createReaction(id, newReaction)
    } catch (error: ResponseError) {
      error.value = error
      userReaction.value = prevReaction
      delayedChangeCount(newReaction, REMOVE_COUNT)
      delayedChangeCount(prevReaction, ADD_COUNT)
    } finally {
      isPending.value = false
      fetchReactions(reactionable)
    }
  }

  async function deleteReaction(id: string, reaction: Emoji) {
    userReaction.value = null
    isPending.value = true
    error.value = null
    delayedChangeCount(reaction, REMOVE_COUNT)
    try {
      await api.deleteReaction(id, reaction)
    } catch (error: ResponseError) {
      error.value = error
      userReaction.value = reaction
      delayedChangeCount(reaction, ADD_COUNT)
    } finally {
      isPending.value = false
      fetchReactions(reactionable)
    }
  }

  return {
    createReaction,
    updateReaction,
    deleteReaction,
    likesCount,
    ideasCount,
    cheersCount,
    userReaction,
    reactions,
  }
}
